import './SalesSubscriptions.css'
import React, { useContext, useEffect, useState } from 'react'
import showNotification from '../../../utils/showNotification'
import { ERROR_HANDLER } from '../../../utils/error-handler'
import axios from 'axios'
import AuthContext from '../../../contexts/AuthContext'
import { Badge, Button, Center, Flex, Loader, Modal, NumberInput, Stack, TextInput, Select, Grid, Switch } from '@mantine/core'
import { IconCheck, IconEdit, IconEye, IconMail, IconPhone, IconTrash, IconUser } from '@tabler/icons-react'
import moment from 'moment'
import { DateInput } from '@mantine/dates'
import { currencyFormat } from '../../../utils/currencyFormat'
import AppContext from '../../../contexts/AppContext/AppContext'

export default function SalesSubscriptionDetails({ form, setUpdateData }) {
   const { authToken } = useContext(AuthContext)
   const { subscriptions } = useContext(AppContext)
   const axiosConfig = {
      headers: {
         Authorization: `Bearer ${authToken}`,
         "Content-Type": "application/json"
      }
   }

   const { payer } = form?.values?.rowData
   const { email, name, lastname, phone } = payer

   const [currentSusbscription, setCurrentSubscription] = useState(false)


   // modals
   const [planDetailsModal, setPlanDetailsModal] = useState(null)
   const [installmentDetails, setInstallmentDetails] = useState(null)
   const [addInstallment, setAddInstallment] = useState(null)
   const [isAddInstallmentModal, setIsAddInstallmentModal] = useState(false)
   const [isUpdated, setIsUpdated] = useState(false)
   const [editInstallment, setEditInstallmentModal] = useState(null)
   const [isEditInstallmentModalOpen, setIsEditInstallmentModalOpen] = useState(false)
   const [editUserPlanSubscription, setEditUserPlanSubscription] = useState(null)
   const [isEditUserPlanSubscriptionModalOpen, setIsEditUserPlanSubscriptionModalOpen] = useState(false)


   // loader by modal
   const [isLoading, setIsLoading] = useState({
      fetching: false,
      deleting: null,
      saving: false
   })

   useEffect(() => {
      const getSubscriptionByEmail = async () => {
         try {
            setIsLoading((prev) => ({ ...prev, fetching: true }))
            if (!email) return null
            const { data } = await axios.get(
               `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/subscriptionDetails/${email}`,
               axiosConfig
            )
            setCurrentSubscription(data)
         } catch (error) {
            console.log(error)
            showNotification({
               color: "red",
               status: "error",
               title: "Operación fallida.",
               message: `No se pudo obtener la suscripción. Detalle: ${ERROR_HANDLER[error.request?.status] || ERROR_HANDLER.defaultError
                  }`,
               autoClose: 12000,
            });
         } finally {
            setIsLoading(prev => ({ ...prev, fetching: false }))
         }
      }
      getSubscriptionByEmail()
   }, [email, isUpdated])


   // functions on modals
   const handleAddInstallment = async (isEdit = false) => {
      const source = isEdit ? editInstallment : addInstallment;

      if (!source) {
         showNotification({
            color: "red",
            status: "error",
            title: "Falta información",
            message: "No se encontraron los datos para agregar o editar la cuota.",
         });
         return;
      }

      const { payId, ...values } = source;


      const jsonData = {
         payId,
         installmentToAdd: values
      }

      const requiredFields = [{ value: 'date', label: "FECHA" }, { value: 'amount', label: "MONTO" }];
      for (const field of requiredFields) {
         if (values[field.value] === undefined) {
            showNotification({
               color: "red",
               status: "error",
               title: "El formulario tiene errores",
               message: `Detalle: el campo ${field.label} es necesario.`
            });
            return;
         }
      }

      try {
         setIsLoading((prev) => ({ ...prev, saving: true }))
         const { data: updatedPayment } = await axios.put(
            `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/addInstallment`,
            jsonData,
            axiosConfig
         )
         showNotification({
            color: "green",
            status: "success",
            title: "¡Excelente! Operación exitosa.",
            message: `Detalle: El pago se ha actualizado correctamente  `,
            autoClose: 12000
         })

         form.setFieldValue('installments.data', updatedPayment.installments.map(item => ({
            due_date: item.date,
            amount: item.amount,
            currency: item.currency,
            _id: item._id,
            state: item.state,
         })))
         setUpdateData(prev => !prev)
         setIsUpdated(!isUpdated)
         isEdit ? setIsEditInstallmentModalOpen(false) : setIsAddInstallmentModal(false)
         isEdit ? setEditInstallmentModal(null) : setAddInstallment(null)
      } catch (error) {
         console.log(error)
         showNotification({
            color: "red",
            status: "error",
            title: "Ha ocurrido un problema al agregar un nuevo pago/cuota.",
            message: `Detalle: ${ERROR_HANDLER[error.request?.status] || ERROR_HANDLER.defaultError}`,
            autoClose: 12000
         })
      } finally {
         setIsLoading(prev => ({ ...prev, saving: false }))
      }
   }

   const handleDeleteInstallment = async (installmentId) => {
      const jsonData = {
         payId: form.values._id,
         installmentToAdd: {
            _id: installmentId
         },
         isDelete: true
      }

      try {
         setIsLoading(prev => ({ ...prev, deleting: installmentId }))
         const { data: updatedPayment } = await axios.put(
            `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/addInstallment`,
            jsonData,
            axiosConfig
         )

         showNotification({
            color: "green",
            status: "success",
            title: "¡Excelente! Operación exitosa.",
            message: `Detalle: El pago se ha eliminado correctamente  `,
         })

         const installmentToAdd = updatedPayment.installments.map(item => ({
            due_date: item.date,
            amount: item.amount,
            currency: item.currency,
            _id: item._id
         }))

         form.setFieldValue('installments.data', installmentToAdd)
         setUpdateData(prev => !prev)
         setIsUpdated(!isUpdated)
         setInstallmentDetails({ data: installmentToAdd })
      } catch (error) {
         console.log(error)
         showNotification({
            color: "red",
            status: "error",
            title: "Operación fallida.",
            message: `No se pudo eliminar el pago. Detalle: ${ERROR_HANDLER[error.request?.status] || ERROR_HANDLER.defaultError
               }`,
            autoClose: 12000,
         });
      } finally {
         setIsLoading(prev => ({ ...prev, deleting: null }))
      }
   }

   const handleUpdateUserSubscription = async () => {
      if (!editUserPlanSubscription) return null

      try {
         setIsLoading(prev => ({ ...prev, saving: true }))

         const jsonData = {
            userEmail: email,
            subscriptionData: {
               type: typeof editUserPlanSubscription.type === 'string' ? editUserPlanSubscription.type : editUserPlanSubscription.type._id,
               startDate: editUserPlanSubscription.startDate,
               endDate: editUserPlanSubscription.endDate,
               is_active: editUserPlanSubscription.is_active,
               free_content: {
                  free_download_available: editUserPlanSubscription?.free_download_available ?? editUserPlanSubscription?.free_content?.free_download_available,
                  last_download_date: editUserPlanSubscription?.last_download_date ?? editUserPlanSubscription?.free_content?.last_download_date,
               }
            }
         }



         await axios.put(
            `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/updateSubscription`,
            jsonData,
            axiosConfig
         )

         setUpdateData(prev => !prev)
         setIsUpdated(!isUpdated)
         setEditUserPlanSubscription(null)
         setIsEditUserPlanSubscriptionModalOpen(false)
         showNotification({
            color: "green",
            status: "success",
            title: "¡Excelente! Operación exitosa.",
            message: `Detalle: La suscripción se ha guardado correctamente.`,
         })

      } catch (error) {
         console.log(error)
         showNotification({
            color: "red",
            status: "error",
            title: "Operación fallida.",
            message: `No se pudo actualizar la suscripción. Detalle: ${ERROR_HANDLER[error.request?.status] || ERROR_HANDLER.defaultError
               }`,
            autoClose: 12000,
         });
      } finally {
         setIsLoading(prev => ({ ...prev, saving: null }))
      }
   }

   // onChange function to every input
   const handleInputChange = (field, value, setter) => {
      setter((prev) => ({
         ...prev,
         [field]: value
      }));
   };


   return (
      <>
         <Stack w='100%'>
            <Stack w='100%'>
               <Flex wrap={'wrap'} gap={10} w='100%' align='start'>
                  <Flex align='center' gap={5}>
                     <IconUser color='var(--color-text-secondary)' />
                     <h3 className='SalesSubscriptionDetails-text-base'>{name} {lastname}</h3>
                  </Flex>
                  <Flex align='center' gap={5}>
                     <IconMail style={{ flexShrink: 0 }} color='var(--color-text-secondary)' />
                     <h3 style={{ textWrap: 'wrap', wordBreak: "break-all" }} className='SalesSubscriptionDetails-text-base'>{email}</h3>
                  </Flex>
                  <Flex align='center' gap={5}>
                     <IconPhone color='var(--color-text-secondary)' />
                     <h3 className='SalesSubscriptionDetails-text-base'>{phone}</h3>
                  </Flex>
               </Flex>

               <Flex gap={10}>
                  <Button
                     variant='outline'
                     color='var(--color-tertiary)'
                     maw='max-content'
                     onClick={() => {
                        setAddInstallment({
                           payId: form?.values._id,
                           state: "PAID",
                           payment_method: "transfer",
                           currency: "ARS"
                        });
                        setIsAddInstallmentModal(true)
                     }} className='SalesSubscriptionDetails--hover'
                  >
                     Agregar pago
                  </Button>
                  <Button
                     maw='max-content'
                     onClick={() => setInstallmentDetails(form?.values.installments)}
                     className='SalesSubscriptionDetails--hover'
                  >
                     Ver pagos
                  </Button>
               </Flex>

               <hr />
               {isLoading?.fetching && <Center>
                  <Loader />
               </Center>}
               {currentSusbscription &&
                  <>
                     <Flex gap={5} align='end' wrap='wrap'>
                        <h3 className='SalesSubscriptionDetails-text-base'>Plan de suscripción:</h3>
                        <Flex gap={2} align='end'>
                           <span className='SalesSubscriptionDetails-text-base'>
                              {currentSusbscription.type.name}
                           </span>
                           <IconEye
                              color='var(--color-text-secondary)'
                              className='SalesSubscriptionDetails--hover'
                              onClick={() => setPlanDetailsModal(currentSusbscription.type)}
                           />
                        </Flex>
                     </Flex>

                     <Flex gap={5} align='center' wrap='wrap'>
                        <h3 className='SalesSubscriptionDetails-text-base'>Estado de la suscripción:</h3>
                        <Badge color={currentSusbscription.is_active ? "var(--color-green)" : "var(--color-red)"}>
                           {currentSusbscription.is_active ? "ACTIVA" : "NO ACTIVA"}
                        </Badge>
                     </Flex>

                     <Flex gap={5} align='end' wrap='wrap'>
                        <h3 className='SalesSubscriptionDetails-text-base'>Suscripto desde:</h3>
                        <span className='SalesSubscriptionDetails-text-base'>{moment(currentSusbscription.startDate).format("DD [de] MMMM [de] YYYY")}</span>
                     </Flex>

                     <Flex gap={5} align='end' wrap='wrap'>
                        <h3 className='SalesSubscriptionDetails-text-base'>Válida hasta:</h3>
                        <span className='SalesSubscriptionDetails-text-base'>{moment(currentSusbscription.endDate).format("DD [de] MMMM [de] YYYY")}</span>
                     </Flex>

                     {form.values.rowData.payment.type === 'TRANSFERENCIA' && <Button onClick={() => {setIsEditUserPlanSubscriptionModalOpen(true);setEditUserPlanSubscription(currentSusbscription)}} variant='outline' color='var(--color-tertiary)' w='max-content' type='button'>Editar</Button>}

                     <hr />

                     <Stack w='100%'>
                        <h3 className='SalesSubscriptionDetails-text-base'>Descargas gratuitas</h3>
                        <Stack w='100%'>
                           <Flex gap={5} align='end'>
                              <h3 className='SalesSubscriptionDetails-text-base'>Restantes:</h3>
                              <span className='SalesSubscriptionDetails-text-base'>{currentSusbscription.free_content.free_download_available}</span>
                           </Flex>

                           <Flex gap={5} align='end'>
                              <h3 className='SalesSubscriptionDetails-text-base'>Última realizada:</h3>
                              <span className='SalesSubscriptionDetails-text-base'>{moment(currentSusbscription.free_content.last_download_date).format("DD [de] MMMM [de] YYYY")}</span>
                           </Flex>
                        </Stack>
                     </Stack>



                  </>
               }
            </Stack>
         </Stack>

         {/* MODALS */}
         {/* allows you to see details from the current subscription */}
         {planDetailsModal &&
            <Modal
               opened={planDetailsModal}
               onClose={() => setPlanDetailsModal(null)}

            >
               <Stack w='100%'>
                  <Flex gap={5} align='end'>
                     <h3 className='SalesSubscriptionDetails-text-base'>Nombre del plan:</h3>
                     <span className='SalesSubscriptionDetails-text-base'>{planDetailsModal.name}</span>
                  </Flex>



                  <Flex gap={5} align='end'>
                     <h3 className='SalesSubscriptionDetails-text-base'>Nivel:</h3>
                     <span className='SalesSubscriptionDetails-text-base'>{planDetailsModal.level}</span>
                  </Flex>

                  <Stack gap={5} align='start'>
                     <h3 className='SalesSubscriptionDetails-text-base'>Precio:</h3>
                     <Stack gap={5}>
                        <span className='SalesSubscriptionDetails-text-base'>USD {planDetailsModal.price.usd}</span>
                        <span className='SalesSubscriptionDetails-text-base'>ARS {planDetailsModal.price.ars}</span>
                     </Stack>
                  </Stack>

                  <Stack gap={5} align='start'>
                     <h3 className='SalesSubscriptionDetails-text-base'>Características</h3>
                     <Stack gap={2}>
                        {planDetailsModal?.features?.map(item => (
                           <Flex key={item} align='center' justify='start' gap={8}>
                              <IconCheck /><span className='SalesSubscriptionDetails-text-base'>{item}</span>
                           </Flex>
                        ))}
                     </Stack>
                  </Stack>
               </Stack>

            </Modal>
         }

         {/* allows you to see and delete installments */}
         {installmentDetails &&
            <Modal
               opened={installmentDetails}
               onClose={() => setInstallmentDetails(null)}
            >
               <Stack w='100%' p={0}>
                  <h3 className='SalesSubscriptionDetails-text-lg'>Pagos ingresados</h3>
                  <Grid>
                     <Grid.Col span={{ base: 6, xs: 4 }} align='start'>
                        <span className='SalesSubscriptionDetails-text-base'>
                           Fecha
                        </span>
                     </Grid.Col>
                     <Grid.Col span={4} align='center'>
                        <span className='SalesSubscriptionDetails-text-base'>
                           Monto
                        </span>
                     </Grid.Col>
                     <Grid.Col span={{ base: 2, xs: 3 }} align='end'>
                        <span className='SalesSubscriptionDetails-text-base'>
                           Estado
                        </span>
                     </Grid.Col>
                     {installmentDetails
                        ? installmentDetails?.data?.map(item => (
                           <React.Fragment key={item._id}>
                              <Grid.Col span={{ base: 6, xs: 4 }} align='start'>

                                 <span className='SalesSubscriptionDetails-text-base'>
                                    {moment(item.due_date).format("DD/MM/YYYY")}
                                 </span>
                              </Grid.Col>
                              <Grid.Col span={4} align='center'>

                                 <span className='SalesSubscriptionDetails-text-base'>
                                    ${currencyFormat(item.amount)} {item.currency?.toUpperCase() === 'ARS' ? "ARS" : "USD"}
                                 </span>
                              </Grid.Col>
                              <Grid.Col align='end' span={{ base: 1, xs: 2 }}>
                                 {item?.state &&

                                    <span className='SalesSubscriptionDetails-text-tiny SalesSubscriptionDetails-custom-badge'>
                                       {item?.state?.toLowerCase() === 'paid' ? "PAGADO" : item?.state?.toLowerCase() === 'pending' ? "PENDIENTE" : "ERROR"}
                                    </span>
                                 }
                              </Grid.Col>
                              <Grid.Col span={2}>
                                 <IconEdit onClick={() => {
                                    setEditInstallmentModal({
                                       payId: form?.values._id,
                                       due_date: item.due_date,
                                       amount: item.amount,
                                       currency: item.currency,
                                       _id: item._id
                                    });
                                    setIsEditInstallmentModalOpen(true)
                                 }} size={20} className='SalesSubscriptionDetails--hover' />
                                 {(isLoading.deleting && isLoading.deleting === item._id)
                                    ? <Loader size={20} />
                                    : <IconTrash
                                       size={20}
                                       className='SalesSubscriptionDetails--hover'
                                       onClick={() => handleDeleteInstallment(item._id)}
                                    />}
                              </Grid.Col>
                           </React.Fragment>
                        ))
                        : <p className='SalesSubscriptionDetails-text-base'>No hay pagos registrados</p>
                     }
                  </Grid>
                  <Button
                     type='button'
                     disabled={isLoading.deleting}
                     onClick={() => setInstallmentDetails(null)}
                  >
                     Cerrar
                  </Button>
               </Stack>
            </Modal>
         }
         {/* allows you to create and add a installment into payment document */}

         {isAddInstallmentModal && (
            <Modal
               opened={isAddInstallmentModal}
               onClose={() => { setIsAddInstallmentModal(false); setAddInstallment(null) }}
            >
               <Stack w="100%" p={0}>
                  <h3 className="SalesSubscriptionDetails-text-lg">Agregar pago o cuota</h3>

                  <Stack>
                     <DateInput
                        label="Fecha del pago"
                        placeholder="Seleccionar fecha"
                        value={addInstallment.date || null}
                        onChange={(date) => handleInputChange('date', date, setAddInstallment)}
                        withAsterisk
                     />

                     <NumberInput
                        label="Monto"
                        placeholder="Ingrese el monto"
                        value={addInstallment.amount || ''}
                        onChange={(value) => handleInputChange('amount', value, setAddInstallment)}
                        withAsterisk
                     />

                     <Select
                        allowDeselect={false}
                        label="Estado del pago"
                        data={[{
                           label: "Pagado",
                           value: "PAID"
                        },
                        {
                           value: "PENDING",
                           label: "Pendiente"
                        }]}
                        defaultValue={addInstallment.state || 'PAID'}
                        onChange={(value) => handleInputChange('state', value, setAddInstallment)}
                     />

                     <Select
                        allowDeselect={false}
                        label="Método de pago"
                        data={[{
                           label: "Transferencia",
                           value: 'transfer'
                        },
                        {
                           label: "Paypal",
                           value: 'paypal'
                        },
                        {
                           label: "Pagos 360",
                           value: 'pagos360'
                        }
                        ]}
                        defaultValue={addInstallment.payment_method || 'transfer'}
                        onChange={(value) => handleInputChange('payment_method', value, setAddInstallment)}
                     />

                     <Select
                        allowDeselect={false}
                        label="Moneda"
                        data={[
                           {
                              label: "ARS",
                              value: 'ARS'
                           },
                           {
                              label: "USD",
                              value: 'USD'
                           },
                        ]}
                        defaultValue={addInstallment.currency || 'USD'}
                        onChange={(value) => handleInputChange('currency', value, setAddInstallment)}
                     />

                     <TextInput
                        label="Descripción"
                        placeholder="Descripción opcional"
                        value={addInstallment.description || ''}
                        onChange={(e) => handleInputChange('description', e.target.value, setAddInstallment)}
                     />

                     <Flex direction={{ base: 'column', xs: 'row' }} w='100%' justify='center'>
                        <Button variant='transparent' w={{ base: '100%', xs: '50%' }} disabled={isLoading.saving} type='button' onClick={() => { setAddInstallment(null); setIsAddInstallmentModal(false) }}>
                           Volver
                        </Button>
                        <Button w={{ base: '100%', xs: '50%' }} disabled={isLoading.saving} type='button' onClick={() => handleAddInstallment(false)}>
                           {isLoading.saving ? <Loader /> : "Confirmar pago"}
                        </Button>
                     </Flex>
                  </Stack>
               </Stack>
            </Modal>

         )}

         {/* allows you to edit a installment already created */}
         {isEditInstallmentModalOpen && <Modal
            opened={isEditInstallmentModalOpen}
            onClose={() => { setEditInstallmentModal(null); setIsEditInstallmentModalOpen(false) }}
         >
            <Stack w='100%' p={0}>
               <h3 className='SalesSubscriptionDetails-text-lg'>Editar pago</h3>
               <Grid>
                  <Grid.Col span={12}>
                     <DateInput
                        label="Fecha del pago"
                        placeholder="Seleccionar fecha"
                        defaultValue={editInstallment.due_date || null}
                        onChange={(date) => handleInputChange('date', date, setEditInstallmentModal)}
                        withAsterisk
                     />
                  </Grid.Col>
                  <Grid.Col span={12}>
                     <NumberInput
                        label="Monto"
                        placeholder="Ingrese el monto"
                        defaultValue={editInstallment.amount || ''}
                        onChange={(value) => handleInputChange('amount', value, setEditInstallmentModal)}
                        withAsterisk
                     />
                  </Grid.Col>
                  <Grid.Col span={12}>
                     <Select
                        allowDeselect={false}
                        label="Moneda"
                        data={[
                           {
                              label: "ARS",
                              value: 'ARS'
                           },
                           {
                              label: "USD",
                              value: 'USD'
                           },
                        ]}
                        defaultValue={editInstallment.currency || 'USD'}
                        onChange={(value) => handleInputChange('currency', value, setEditInstallmentModal)}
                     />
                  </Grid.Col>

                  <Grid.Col span={12} justify='center' align='center'>
                     <Button
                        w={{ base: "100%", xs: '50%' }}
                        onClick={() => { setEditInstallmentModal(null); setIsEditInstallmentModalOpen(false) }}
                        variant='transparent' type='button'
                     >
                        Volver

                     </Button>
                     <Button
                        w={{ base: "100%", xs: '50%' }}
                        onClick={() => handleAddInstallment(true)}
                        type='button'
                     >
                        Guardar cambios

                     </Button>
                  </Grid.Col>
               </Grid>
            </Stack>
         </Modal>}

         {/* allows you to update user current subscription  */}
         {(isEditUserPlanSubscriptionModalOpen) && <Modal
            opened={isEditUserPlanSubscriptionModalOpen}
            onClose={() => {setIsEditUserPlanSubscriptionModalOpen(false);setEditUserPlanSubscription(null)}}
         >
            <Stack w="100%" p={0}>
               <h3 className="SalesSubscriptionDetails-text-lg">Editar suscripción</h3>
               <Grid>
                  <Grid.Col span={12}>
                     <Switch
                        label='Estado'
                        defaultChecked={editUserPlanSubscription.is_active}
                        onChange={(event) => handleInputChange('is_active', event.currentTarget.checked, setEditUserPlanSubscription)}
                     />
                  </Grid.Col>
                  <Grid.Col span={12}>
                     <Select
                        label='Plan de suscripción'
                        allowDeselect={false}
                        data={subscriptions}
                        defaultValue={editUserPlanSubscription.type._id}
                        onChange={(value) => handleInputChange('type', value, setEditUserPlanSubscription)}
                     />
                  </Grid.Col>
                  <Grid.Col span={12}>
                     <DateInput
                        label="Fecha del inicio"
                        placeholder="Seleccionar fecha"
                        defaultValue={moment(editUserPlanSubscription.startDate).toDate() || null}
                        onChange={(date) => handleInputChange('startDate', date, setEditUserPlanSubscription)}
                     />
                  </Grid.Col>
                  <Grid.Col span={12}>
                     <DateInput
                        label="Fecha de vencimiento"
                        placeholder="Seleccionar fecha"
                        defaultValue={moment(editUserPlanSubscription.endDate).toDate() || null}
                        onChange={(date) => handleInputChange('endDate', date, setEditUserPlanSubscription)}
                     />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }} style={{ alignSelf: 'end' }}>
                     <NumberInput
                        min={0}
                        max={100}
                        label="Descargas gratuitas"
                        defaultValue={editUserPlanSubscription.free_content.free_download_available || 0}
                        onChange={(value) => handleInputChange('free_download_available', Number(value), setEditUserPlanSubscription)}
                     />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                     <DateInput
                        label="Fecha de última descarga realizada"
                        defaultValue={moment(editUserPlanSubscription.free_content.last_download_date).toDate() || null}
                        onChange={(date) => handleInputChange('last_download_date', date, setEditUserPlanSubscription)}
                     />
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, xs: 6 }}>
                     <Button w='100%' variant='outline' onClick={() => {setIsEditUserPlanSubscriptionModalOpen(false);setEditUserPlanSubscription(null)}} disabled={isLoading.saving} type='button'>Volver</Button>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, xs: 6 }}>
                     <Button w='100%' type='button' onClick={() => handleUpdateUserSubscription()} disabled={isLoading.saving}>{isLoading.saving ? <Loader /> : "Guardar cambios"}</Button>
                  </Grid.Col>
               </Grid>
            </Stack>

         </Modal>}
      </>
   )
}